/* eslint-disable camelcase */

import axios from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { ButtonGroup, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { updateUser } from '../../../actions/auth.actions';
import Button from '../../../shared/components/Button';
import FormControl from '../../../shared/components/Form/FormControl';
import ResponsiveHeader from '../../../shared/components/ResponsiveHeader';
import useBreakpoint from '../../../shared/hooks/useBreakpoint';
import useIsMounted from '../../../shared/hooks/useIsMounted';
import i18n from '../../../utils/i18n';

const validationSchema = Yup.object().shape({
  instagram: Yup.string(),
  tiktok: Yup.string(),
  youtube: Yup.string().url(i18n.t('influencers.common.forms.is_not_valid_url')),
  linkedin: Yup.string(),
});

const SignUpStep2 = ({
  goToStep,
}) => {
  const user = useSelector(store => get(store, 'auth.user'));
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const breakpoint = useBreakpoint();
  const [state, setState] = useState({
    showPassword: false,
    showConfirmationPassword: false,
    loading: false
  });

  const formik = useFormik({
    initialValues: {
      instagram: '',
      tiktok: '',
      youtube: '',
      linkedin: '',
    },
    validationSchema,
    onSubmit: values => {
      setState({ ...state, loading: true });
      const updateSocialNetwork = (payload) => axios.put(`/api/v1/influencers/users/${user.id}`, {
        user: payload
      });

      const promises = [];

      if (values.instagram) {
        promises.push(updateSocialNetwork({
          inst_username: values.instagram
        }));
      }
      if (values.tiktok) {
        promises.push(updateSocialNetwork({
          tikt_username: values.tiktok
        }));
      }
      if (values.youtube) {
        promises.push(updateSocialNetwork({
          yout_url: values.youtube
        }));
      }
      if (values.linkedin) {
        promises.push(updateSocialNetwork({
          link_username: values.linkedin
        }));
      }

      Promise.allSettled(promises)
        .then((responses) => {
          const usernames = {};
          const errors = {};

          responses
            .forEach(response => {

              if (response.status === 'fulfilled') {
                const data = get(response, 'value.data');
                console.log(data);

                const yout_username = get(data, 'user.social.yout.nickname');
                const tikt_username = get(data, 'user.social.tikt.nickname');
                const inst_username = get(data, 'user.social.inst.nickname');
                const link_username = get(data, 'user.social.link.nickname');

                if (yout_username) usernames.yout = { nickname: yout_username };
                if (tikt_username) usernames.tikt = { nickname: tikt_username };
                if (inst_username) usernames.inst = { nickname: inst_username };
                if (link_username) usernames.link = { nickname: link_username };

                if (!yout_username && !tikt_username && !inst_username && !link_username) {
                  if (values.instagram) errors.instagram = 'not set';
                  if (values.tiktok) errors.tiktok = 'not set';
                  if (values.youtube) errors.youtube = 'not set';
                  if (values.linkedin) errors.linkedin = 'not set';
                }
                if (data.error) errors.instagram = 'unknown error';
              }

              if (response.status === 'rejected') {
                const data = get(response, 'reason.response.data');

                const yout_error = get(data, 'errors.yout_id[0]');
                const tikt_error = get(data, 'errors.tikt_id[0]');
                const inst_error = get(data, 'errors.inst_id[0]');
                const link_error = get(data, 'errors.link_id[0]');

                if (yout_error) errors.youtube = yout_error;
                if (tikt_error) errors.tiktok = tikt_error;
                if (inst_error) errors.instagram = inst_error;
                if (link_error) errors.linkedin = link_error;
              }
            });

          if (!Object.keys(usernames).length) throw errors;

          dispatch(updateUser({
            social: usernames
          }));

          goToStep(3);
        }).catch((err) => {
          console.log(err);
          if (get(err, 'instagram')) {
            formik.setFieldError('instagram', i18n.t('influencers.common.username_already_connected', { username: values.instagram }));
          }
          if (get(err, 'tiktok')) {
            formik.setFieldError('tiktok', i18n.t('influencers.common.username_already_connected', { username: values.tiktok }));
          }
          if (get(err, 'youtube')) {
            formik.setFieldError('youtube', i18n.t('influencers.common.username_already_connected', { username: values.youtube }));
          }
          if (get(err, 'linkedin')) {
            formik.setFieldError('linkedin', i18n.t('influencers.common.username_already_connected', { username: values.linkedin }));
          }

        }).finally(() => {
          if (!isMounted) return;
          setState({ ...state, loading: false });
        });
    },
  });

  return (<Row>
    <Col xs={12} lg={5}>
      <div className="body-3 mb-2">
        {i18n.t('influencers.common.step_of', {
          now: 2,
          end: 3
        })}
      </div>
      <ResponsiveHeader xs="h3" lg="h2">
        {i18n.t('influencers.pages.sign_up.social_networks')}
      </ResponsiveHeader>
      <div className="body-1 mb-3">
        {i18n.t('influencers.pages.sign_up.social_networks_desc')}
      </div>
      <div className="body-1 mb-4">
        <div className="d-flex text-3">
          <i className='fal fa-lock mr-3 fa-1x' />
          {i18n.t('influencers.pages.sign_up.privacy_desc')}
        </div>
      </div>
    </Col>
    <Col xs={12} lg={{
      span: 6,
      offset: 1
    }}>
      <Form onSubmit={formik.handleSubmit}>
        <Row>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>
                <i className='fab fa-instagram mr-2' />
                {i18n.t('influencers.common.instagram')}
              </Form.Label>
              <FormControl
                type='text'
                name="instagram"
                data-cy="instagram"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.instagram}
                error={formik.touched.instagram && Boolean(formik.errors.instagram)} />
              <Form.Text className="text-muted danger-text">
                {formik.touched.instagram && formik.errors.instagram}
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group>
              <Form.Label>
                <i className='fab fa-tiktok mr-2' />
                {i18n.t('influencers.common.tiktok')}
              </Form.Label>
              <FormControl
                type='text'
                name="tiktok"
                data-cy="tiktok"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.tiktok}
                error={formik.touched.tiktok && Boolean(formik.errors.tiktok)} />
              <Form.Text className="text-muted danger-text">
                {formik.touched.tiktok && formik.errors.tiktok}
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group>
              <Form.Label>
                <i className='fab fa-youtube mr-2' />
                {i18n.t('influencers.common.youtube')}
              </Form.Label>
              <FormControl
                type='text'
                placeholder={i18n.t('influencers.common.youtube_placeholder')}
                name="youtube"
                data-cy="youtube"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.youtube}
                error={formik.touched.youtube && Boolean(formik.errors.youtube)} />
              <Form.Text className="text-muted danger-text">
                {formik.touched.youtube && formik.errors.youtube}
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs={12}>
            <Form.Group>
              <Form.Label>
                <i className='fab fa-linkedin mr-2' />
                {i18n.t('influencers.common.linkedin')}
              </Form.Label>
              <FormControl
                type='text'
                name="linkedin"
                data-cy="linkedin"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.linkedin}
                error={formik.touched.linkedin && Boolean(formik.errors.linkedin)} />
              <Form.Text className="text-muted danger-text">
                {formik.touched.linkedin && formik.errors.linkedin}
              </Form.Text>
            </Form.Group>
          </Col>

        </Row>

        <hr className={classNames('my-6', {
          noMargin: breakpoint === 'xs'
        })} />
        <div className='flex flex-column align-items-end'>
          <ButtonGroup className="mb-4">
            <Button
              variant="primary"
              type="submit"
              disabled={!formik.dirty || state.loading || !formik.isValid}>
              {state.loading
                ? <Spinner animation="border" size="sm" />
                : i18n.t('influencers.common.next')
              }
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </Col>
  </Row>);
};

SignUpStep2.propTypes = {
  goToStep: PropTypes.func.isRequired,
};

export default SignUpStep2;
